import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { fetchSiteSettings } from "@/lib/api";

const DEFAULT_TITLE = "Airline Operators Association of Somalia | AOA Somalia";
const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export default function SEO({
  title,
  description,
  ogImage,
  path = "/",
  type = "website",
  breadcrumb,
}) {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    fetchSiteSettings().then(setSettings).catch(() => {});
  }, []);

  const pageTitle = title ? `${title} | AOA Somalia` : DEFAULT_TITLE;
  const desc =
    description ||
    "AOA — Airline Operators Association of Somalia — is a voluntary, non-governmental aviation coordination platform supporting safe, secure and orderly air transport in Somalia.";
  const url = `${ORIGIN}${path}`;
  const img = ogImage
    ? (ogImage.startsWith("http") ? ogImage : `${ORIGIN}${ogImage}`)
    : `${ORIGIN}/imagery/airliner.png`;

  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Airline Operators Association of Somalia",
    alternateName: ["AOA Somalia", "AOA"],
    url: ORIGIN,
    logo: `${ORIGIN}/logos/aoa_logo_royal_blue.png`,
    description: desc,
    address: {
      "@type": "PostalAddress",
      streetAddress:
        settings?.contact?.address ||
        "HQ - Main Terminal, Aden Abdulle International Airport, Wadajir District",
      addressLocality: "Mogadishu",
      addressCountry: "SO",
    },
    contactPoint: (() => {
      const phones = settings?.contact?.phones || [
        "+252 615 963 220",
        "+252 615 176 876",
        "+252 615 884 936",
        "+252 615 637 878",
      ];
      const email = settings?.contact?.email || "info@aoa.so";
      return phones.map((tel, i) => ({
        "@type": "ContactPoint",
        contactType: "general",
        telephone: tel.replace(/\s+/g, ""),
        ...(i === 0 ? { email } : {}),
        availableLanguage: ["en", "so", "ar"],
        areaServed: "SO",
      }));
    })(),
    sameAs: settings?.socials ? Object.values(settings.socials).filter(Boolean) : [],
  };

  const websiteLd = path === "/" ? {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Airline Operators Association of Somalia",
    alternateName: "AOA Somalia",
    url: ORIGIN,
    potentialAction: {
      "@type": "SearchAction",
      target: `${ORIGIN}/news?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  } : null;

  // BreadcrumbList on inner pages (anything except /)
  const crumbs = breadcrumb || (path && path !== "/" ? [
    { name: "Home", path: "/" },
    { name: title || path.replace("/", ""), path },
  ] : null);
  const breadcrumbLd = crumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${ORIGIN}${c.path}`,
    })),
  } : null;

  return (
    <Helmet>
      <html lang="en" />
      <title>{pageTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content="AOA Somalia" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      {websiteLd && (
        <script type="application/ld+json">{JSON.stringify(websiteLd)}</script>
      )}
      {breadcrumbLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      )}
    </Helmet>
  );
}
